import { SortDirectionEnum } from '../../../legacy/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from 'components/common';
import { EditIcon } from 'components/common/Icons';
import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { getPageURLWithSection, PageURL } from 'router/pageURL';
import { btlApplicationFormApi } from '../../../api/btlApplicationForm';
import { useBtlApplications } from '../../../queries';
import { BtlApplicationFormPageEnum } from '../../BtlApplicationFormPage/types';
import { FormattedTableCell } from '../ApplicationsTable/FormattedTableCell';
import { BtlApplicationForListingDto, IApplicationsAnalystTableProps, tableColumnsNames } from './types';
import { allCapsToTitleCase, prettifyEnum } from 'common/helpers';

export const BtlApplicationsTable: FC<IApplicationsAnalystTableProps> = ({ searchInputValue }) => {
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.DESC);
  const [orderBy, setOrderBy] = useState<keyof BtlApplicationForListingDto>('createdAt');
  const { data: allBtlApplications, isLoading } = useBtlApplications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const formattedApplications = allBtlApplications?.map((application: BtlApplicationForListingDto) => ({
    id: application.id,
    case: application.title,
    purpose: allCapsToTitleCase(application.purpose),
    financingAmount: application.financingAmount,
    referenceNumber: application.referenceNumber,
    applicant: application.applicant,
    coApplicant: application.coApplicant,
    status: prettifyEnum(application.status),
    createdAt: application.createdAt,
  }));

  const sortedRows = formattedApplications?.sort((a: any, b: any) => {
    if (a[orderBy] < b[orderBy]) {
      return order === SortDirectionEnum.ASC ? -1 : 1;
    }

    if (a[orderBy] > b[orderBy]) {
      return order === SortDirectionEnum.ASC ? 1 : -1;
    }
    return 0;
  });

  const filteredBySearchInputValue = sortedRows?.filter((row: BtlApplicationForListingDto) => {
    const rowValues = Object.values(row);
    return rowValues.some((value: any) => value?.toString()?.toLowerCase().includes(searchInputValue?.toLowerCase()));
  });

  const sortHandler = (column: keyof BtlApplicationForListingDto) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === column;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(column);
  };

  const applicationDetailsPreloadHandler = async (applicationId: string | number) => {
    await queryClient.prefetchQuery(['btl-application-in-progress', applicationId], () =>
      btlApplicationFormApi.getApplicationInProgress(String(applicationId)),
    );

    const editBtlApplicationUrl = getPageURLWithSection(
      PageURL.BTL_APPLICATION_EDIT,
      { id: applicationId },
      { section: BtlApplicationFormPageEnum.CASE_SUMMARY },
    );
    navigate(editBtlApplicationUrl);
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table aria-label='collapsible table'>
          <TableHead>
            <TableRow>
              <TableCell />
              {tableColumnsNames.map(columnName => (
                <TableCell key={columnName}>
                  <TableSortLabel
                    data-value={columnName}
                    active={orderBy === columnName}
                    direction={order}
                    onClick={() => sortHandler(columnName as keyof BtlApplicationForListingDto)}
                  >
                    <FormattedMessage id={`application.${columnName}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length}>
            {filteredBySearchInputValue?.length ? (
              filteredBySearchInputValue.map((row: BtlApplicationForListingDto, index: number) => (
                <TableRow key={index}>
                  <TableCell />
                  {Object.entries(row)
                    .filter(value => value[0] !== 'id' && value[0] !== 'coApplicant')
                    .map(([objectKey, value]: [string, any]) => (
                      <TableCell key={objectKey}>
                        <FormattedTableCell objectKey={objectKey} value={value} />
                      </TableCell>
                    ))}
                  <TableCell>
                    <button
                      className={'cursor-pointer border-0 bg-transparent'}
                      onClick={() => applicationDetailsPreloadHandler(row.id)}
                    >
                      <EditIcon htmlColor={'grey'} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={tableColumnsNames.length}>
                  <FormattedMessage id={'common.itemsNotFound'} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
